/**
* ServicePointsBlock — แสดง "จุดนัดพบ / พื้นที่ให้บริการ" ในจังหวัดหรือเขตนั้น ๆ
*
* GOOGLE COMPLIANCE (ห้ามแก้ให้อ่อนลง):
* - จุดที่ไม่มีสำนักงานจริง (`serviceAreaOnly`) จะแสดงเป็น "จุดนัดพบ" เท่านั้น
* ไม่มีเลขที่/รหัสไปรษณีย์ และไม่ถูก emit เป็น PostalAddress ใน JSON-LD
* - เฉพาะจุดที่ผ่าน `hasPostalAddress()` เท่านั้นที่แสดงที่อยู่เต็ม
*/
import { Link } from "@tanstack/react-router";
import {
  hasPostalAddress,
  locationLine,
  BRANCH_TYPE_LABEL,
  SERVICE_FOCUS_LABEL,
  SERVICE_FOCUS_PATH,
  type Branch,
} from "@/data/branches/index";

export function ServicePointsBlock({
  points,
  areaLabel,
  limit = 24,
}: {
  points: Branch[];
  areaLabel: string;
  limit?: number;
}) {
  if (points.length === 0) return null;
  const shown = points.slice(0, limit);
  const rest = points.length - shown.length;

  return (
    <section className="pt-4">
      <h2 className="text-2xl font-semibold">จุดนัดพบและพื้นที่ให้บริการใน{areaLabel}</h2>
      <p className="mt-2 text-sm text-muted-foreground answer-summary">
        เรามีจุดนัดพบสำหรับรับ–ส่งเอกสารและลงนามต่อหน้าทนายความรับรองลายมือชื่อรวม{" "}
        {points.length} จุดใน{areaLabel} ทุกจุดเป็นการให้บริการแบบ
        <strong> นัดหมายล่วงหน้า / นอกสถานที่</strong> (ไม่ใช่สำนักงานประจำ)
        โดยสำนักงานจดทะเบียนจริงของเราอยู่ที่กรุงเทพมหานคร (วังทองหลาง)
      </p>

      <ul className="mt-4 grid gap-3 md:grid-cols-2">
        {shown.map((b) => (
          <li key={b.id} className="rounded-lg border border-border/60 bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-medium text-foreground">{b.shortName || b.name}</h3>
              <span className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                {hasPostalAddress(b) ? BRANCH_TYPE_LABEL[b.branchType] : "นัดหมายล่วงหน้า"}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{locationLine(b)}</p>
            {b.transit.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                เดินทาง: {b.transit.slice(0, 3).join(" · ")}
              </p>
            )}
            {b.mapUrl && (
              <p className="mt-1 text-xs">
                <a href={b.mapUrl} target="_blank" rel="noopener noreferrer" className="underline">
                  ดูแผนที่ Google Maps →
                </a>
              </p>
            )}
            {b.landmarks.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                ใกล้: {b.landmarks.slice(0, 3).join(" · ")}
              </p>
            )}
            {b.serviceFocus.length > 0 && (
              <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {b.serviceFocus.slice(0, 5).map((f) => (
                  <Link key={f} to={SERVICE_FOCUS_PATH[f]} className="underline hover:no-underline">
                    {SERVICE_FOCUS_LABEL[f]}
                  </Link>
                ))}
              </p>
            )}
          </li>
        ))}
      </ul>

      {rest > 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          และอีก {rest} จุดใน{areaLabel} —{" "}
          <Link to="/branches" className="underline">
            ดูรายการจุดบริการทั้งหมด
          </Link>
        </p>
      )}
    </section>
  );
}
